import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink, Router, ActivatedRoute } from '@angular/router';
import { AlertComponent } from '@shared/alert/alert.component';
import { AuthenticationService } from '@core/services/auth.service';
import { convertEmailToLowercase } from '@core/utils/form.utils';
import {
  getPasswordRequirements,
  passwordStrengthValidator,
} from '@core/utils/password-validation.utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

enum RecoveryStep {
  EMAIL = 1,
  CONFIRMATION = 2,
  PASSWORD = 3
}

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, AlertComponent, FontAwesomeModule],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.css'
})
export class ForgotPasswordComponent implements OnInit {
  forgotPasswordForm: FormGroup;
  passwordForm: FormGroup;
  show = false;
  toastTitle = '';
  toastMessage = '';
  toastType: 'info' | 'success' | 'warning' | 'alert' = 'info';
  currentStep = RecoveryStep.EMAIL;
  recoveryEmail = '';
  resetToken = '';
  isLoading = false;

  // FontAwesome icons
  faChevronRight = faChevronRight;

  private toastTimer: any = null;

  get RecoveryStep() {
    return RecoveryStep;
  }

  constructor(
    private fb: FormBuilder, 
    private authService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, passwordStrengthValidator()]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      const email = params['email'];
      
      if (token && email) {
        this.resetToken = token;
        this.recoveryEmail = decodeURIComponent(email);
        this.currentStep = RecoveryStep.PASSWORD;
      }
    });
  }

  passwordMatchValidator = (control: any) => {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');
    
    if (!password || !confirmPassword) {
      return null;
    }

    if (password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      if (confirmPassword.errors?.['passwordMismatch']) {
        delete confirmPassword.errors['passwordMismatch'];
        if (Object.keys(confirmPassword.errors).length === 0) {
          confirmPassword.setErrors(null);
        }
      }
    }
    
    return null;
  };

  getPasswordRequirements() {
    return getPasswordRequirements(this.passwordForm.get('password')?.value || '');
  }

  requestPasswordReset() {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const email = this.forgotPasswordForm.get('email')?.value;
    this.recoveryEmail = email;

    this.authService.requestPasswordRecovery(email).subscribe({
      next: () => {
        this.isLoading = false;
        this.showToast('success', 'Correo enviado', 'Te hemos enviado un enlace de recuperación a tu correo electrónico');
        this.currentStep = RecoveryStep.CONFIRMATION;
      },
      error: (error) => {
        this.isLoading = false;
        const errorMessage = error?.error?.message || 'Ha ocurrido un error al enviar el correo de recuperación';
        this.showToast('alert', 'Error', errorMessage);
      }
    });
  }

  resetPassword() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const password = this.passwordForm.get('password')?.value;
    const confirmPassword = this.passwordForm.get('confirmPassword')?.value;

    this.authService.resetPassword(this.recoveryEmail, this.resetToken, password, confirmPassword).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/'], { 
          queryParams: { 
            passwordReset: 'true',
            message: 'Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión.'
          } 
        });
      },
      error: (error) => {
        this.isLoading = false;
        const errorMessage = error?.error?.message || 'Ha ocurrido un error al actualizar la contraseña';
        this.showToast('alert', 'Error', errorMessage);
      }
    });
  }

  showToast(
    type: 'info' | 'success' | 'warning' | 'alert',
    title: string,
    message: string,
    duration: number = 4000
  ) {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastType = type;
    this.toastTitle = title;
    this.toastMessage = message;

    this.show = false;

    requestAnimationFrame(() => {
      this.show = true;

      this.toastTimer = setTimeout(() => {
        this.show = false;
        this.toastTimer = null;
      }, duration);
    });
  }

  // Convertir email a minúsculas automáticamente
  convertToLowercase(event: any): void {
    convertEmailToLowercase(event, this.forgotPasswordForm);
  }
}
